import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getExternalCalendars } from 'api/calendar';
import ReAuthIntegrationModal from 'components/Modal/ReAuthIntegrationModal';
import APModal from 'components/Modal/APModal';
import { usePrefs } from 'hooks/usePrefs';
import Scheduler from '../SchedulerEditor';
import { ZapierIntegration } from './Zapier';
import IntegrationCard from './IntegrationCard';

import './integrations.scss';

const params = new URLSearchParams(window.location.search);
const isConnected = params.get('account_connected') === 'true';

const Integrations = () => {
  const location = useLocation();
  const [connectedModalOpen, setConnectedModalOpen] = useState(isConnected);

  const { updateCalendarPrefs } = usePrefs();

  const userInfo = useSelector(state => state.global.userInfo);
  const { personas, integrations, integrationsEnabled } = userInfo || {};
  const { isOrgOwner, isOrgMember } = personas || {};
  const expiredIntegration = integrations?.find(integration => integration?.grantStatus === 'expired');

  if (location.pathname.includes('/scheduler-editor')) {
    return (<Scheduler />);
  }

  // The login calendars likely haven't been created yet since the task is delegated to a worker,
  // so we poll for them until they are returned to correctly update the preferences.
  useEffect(() => {
    if (isConnected) {
      setExternalProjectsPref(updateCalendarPrefs);
    }
  }, []);

  return (
    <>
      <div className="integration-list">
        {(integrationsEnabled && (isOrgOwner || isOrgMember)) && (
          <>
            <IntegrationCard />
          </>
        )}
        <ZapierIntegration />
      </div>
      <ReAuthIntegrationModal expiredIntegration={expiredIntegration} />
      <APModal
        title="You’re all set!"
        name="account-connected"
        open={connectedModalOpen}
        onClose={() => setConnectedModalOpen(false)}
        actions={(
          <button
            onClick={() => setConnectedModalOpen(false)}
            className="button"
          >
            Got it
          </button>
        )}
      >
        <div>
          <p>Now that you're connected, you have access to new calendar, scheduling and email features:</p>
          <ul>
            <li>Head on over to your Account Calendar to manage your calendar sync preferences
              and set up your scheduling assistant.</li>
            <li>Visit your Lead Manager to send and receive stand alone emails for each lead.</li>
          </ul>
          <p>Pro Tip: Avoid duplicates! Now that you can manage project calendar syncing (and more)
            from your main Account Calendar, visit your external calendar to disconnect any individual
            Aisle Planner project calendars you may have already synced.</p>
        </div>
      </APModal>
    </>

  );
};

// Polls for external calendars to update the login preferences.
const setExternalProjectsPref = async (updateCalendarPrefs) => {
  try {
    const response = await getExternalCalendars();
    if (response.ok) {
      const externalCalendars = response.data;
      // We only want external calendars that have already been committed to the login_calendars table.
      const loginCalendars = externalCalendars.filter(ec => ec.login_calendar_id);
      // exit condition.
      if (loginCalendars.length > 0) {
        const loginCalendarsById = Object.fromEntries(loginCalendars.map(lc => [lc.login_calendar_id, true]));
        updateCalendarPrefs('externalCalendarsDisplay', loginCalendarsById);
        return;
      }
    }
    setTimeout(() => setExternalProjectsPref(updateCalendarPrefs), 2000);
  } catch {
    // Just keep retrying.
    setTimeout(() => setExternalProjectsPref(updateCalendarPrefs), 2000);
  }
};

export default Integrations;
